/**
 * CampaignPerformancePage — Phase 8 measure surface (audit deliverable 06C).
 *
 * Per-campaign revenue-attribution deep dive. Read-only analytics over the
 * marketing pillar's broadcast campaigns. Deep-links back to the compose
 * surface (the action surface) for fixes, per audit 04 §2.1 measure rules.
 *
 * Data source:
 *   GET /api/admin/email-analytics/revenue/campaigns?period=<today|Nd|custom&from&to>
 *     → { campaigns: [{ campaign_id, campaign_name, status, sent_at,
 *                        total_sent, revenue_cents, revenue_dollars,
 *                        attributed_events }] }
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Alert,
  Button,
  Chip,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import PillarPageShell from "../shared/PillarPageShell";
import TimeRangeFilter from "../../common/TimeRangeFilter";
import useTimeRangeParam from "../../common/useTimeRangeParam";
import { emailAnalyticsQuery } from "../shared/emailAnalyticsRange";
import { describeRange } from "../../common/timeRange";
import apiService from "../../../services/api";

interface CampaignRow {
  campaign_id: string;
  campaign_name: string | null;
  status: string | null;
  sent_at: string | null;
  total_sent: number | null;
  revenue_cents: number | null;
  revenue_dollars: number | null;
  attributed_events: number | null;
}

const ACCENT = "#1A5F7A"; // measure navy

/** Sum a numeric field across rows; returns null when no rows have a value. */
const sumField = (
  rows: CampaignRow[],
  key: keyof CampaignRow,
): number | null => {
  let saw = false;
  let acc = 0;
  for (const r of rows) {
    const v = r[key];
    if (typeof v === "number" && !Number.isNaN(v)) {
      saw = true;
      acc += v;
    }
  }
  return saw ? acc : null;
};

const fmtInt = (n: number | null | undefined): string =>
  n != null && !Number.isNaN(n) ? n.toLocaleString() : "—";

const fmtUsd = (n: number | null | undefined): string =>
  n != null && !Number.isNaN(n)
    ? n.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "—";

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/** Map a campaign status to an MUI Chip color + display label. */
const statusChip = (
  status: string | null,
): { color: "default" | "success" | "warning" | "info"; label: string } => {
  const s = (status ?? "").toLowerCase();
  if (s === "sent" || s === "completed" || s === "delivered")
    return { color: "success", label: status ?? "" };
  if (s === "sending" || s === "scheduled" || s === "queued")
    return { color: "info", label: status ?? "" };
  if (s === "draft" || s === "paused")
    return { color: "warning", label: status ?? "" };
  return { color: "default", label: status ? status : "—" };
};

const KpiCard: React.FC<{
  label: string;
  value: string;
  caption?: string;
}> = ({ label, value, caption }) => (
  <Card sx={{ borderRadius: 2, height: "100%" }}>
    <CardContent>
      <Typography variant="overline" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ color: ACCENT, mt: 1, fontWeight: 700 }}>
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {caption}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const CampaignPerformancePage: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useTimeRangeParam({ type: "30d" });
  const [campaigns, setCampaigns] = useState<CampaignRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res: any = await apiService.get(
          `/api/admin/email-analytics/revenue/campaigns?${emailAnalyticsQuery(period)}`,
        );
        if (!mounted) return;
        // Tolerate { data: { campaigns } }, { campaigns }, or a bare array.
        const payload = res?.data ?? res;
        const list: CampaignRow[] = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.campaigns)
            ? payload.campaigns
            : Array.isArray(payload?.data?.campaigns)
              ? payload.data.campaigns
              : [];
        setCampaigns(list);
      } catch (e: any) {
        if (mounted) setError(e?.message ?? "Failed to load campaign performance");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [period]);

  const rows = campaigns ?? [];
  const totalCampaigns = campaigns != null ? rows.length : null;
  const totalSent = sumField(rows, "total_sent");
  const totalRevenue = sumField(rows, "revenue_dollars");

  return (
    <PillarPageShell
      pillar="measure"
      title="Campaign Performance"
      subtitle={`Per-campaign revenue attribution across ${describeRange(period)}. Read-only — deep-link to compose to act on a campaign.`}
      rightActions={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <TimeRangeFilter value={period} onChange={setPeriod} />
        <Button
          variant="contained"
          size="small"
          onClick={() =>
            navigate("/dashboard/communications/marketing/compose")
          }
          sx={{
            bgcolor: ACCENT,
            "&:hover": { bgcolor: "#15506a" },
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Manage →
        </Button>
        </Box>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* KPI cards */}
      {loading ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={110} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              label="Campaigns"
              value={fmtInt(totalCampaigns)}
              caption="In the last 30 days"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              label="Total Sent"
              value={fmtInt(totalSent)}
              caption="Sum of sends across campaigns"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              label="Total Revenue"
              value={fmtUsd(totalRevenue)}
              caption="Attributed revenue (30d)"
            />
          </Grid>
        </Grid>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Campaign table */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography
            variant="overline"
            sx={{ color: "text.secondary", display: "block", mb: 1 }}
          >
            Campaigns
          </Typography>

          {loading ? (
            <Box>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={44} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                No campaigns with attribution data in the last 30 days.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Campaign</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Sent
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Revenue
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Attributed
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Sent at</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((c) => {
                    const chip = statusChip(c.status);
                    const rev = c.revenue_dollars;
                    return (
                      <TableRow key={c.campaign_id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {c.campaign_name || "—"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={chip.color}
                            label={chip.label}
                            variant={chip.color === "default" ? "outlined" : "filled"}
                            sx={{ textTransform: "capitalize" }}
                          />
                        </TableCell>
                        <TableCell align="right">{fmtInt(c.total_sent)}</TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 600,
                            color:
                              rev != null && rev > 0
                                ? "#146152"
                                : "text.secondary",
                          }}
                        >
                          {fmtUsd(rev)}
                        </TableCell>
                        <TableCell align="right">
                          {fmtInt(c.attributed_events)}
                        </TableCell>
                        <TableCell sx={{ color: "text.secondary" }}>
                          {fmtDate(c.sent_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </PillarPageShell>
  );
};

export default CampaignPerformancePage;
